import * as React from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { getQuestion } from "@/services/non-financial";
import { toast } from "sonner";
import TableSkeleton from "@/components/skeleton/TableSkeleton";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";

import { Input } from "@/components/ui/input";
import { postAnswer } from "@/services/non-financial";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface NonFinancialDetail {
  id: string;
  index: number;
  titleId: string;
  formId: string;
  question: string;
}

interface NonFinancialTitle {
  id: string;
  index: number;
  formId: string;
  title: string;
  nonFinancialDetails: NonFinancialDetail[];
}

interface QuestionResponse {
  status: boolean;
  message: string;
  data: {
    id: string;
    name: string;
    coreSectorId: string;
    startDate: string;
    expiryDate: string;
    yearly: number;
    createdBy: string;
    updatedBy: string;
    createdAt: string;
    updatedAt: string;
    coreSector: {
      id: string;
      coreSector: string;
    };
    nonFinancialTitles: NonFinancialTitle[];
  };
}

interface FormState {
  [key: string]: {
    answer: number | string;
    description: string;
  };
}
export const Route = createFileRoute(
  "/_layout-private/report/non-financial-assessment/answer/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const ID = String(localStorage.getItem("Question"));
  const [formData, setFormData] = useState<FormState>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: questionData, isLoading } = useQuery<QuestionResponse>({
    queryKey: ["boards", ID],
    queryFn: () => getQuestion(ID),
  });

  const nonFinancialTitles = questionData?.data?.nonFinancialTitles;

  // Save formId to localStorage
  React.useEffect(() => {
    if (nonFinancialTitles?.[0]?.formId) {
      localStorage.setItem("formId", nonFinancialTitles[0].formId);
    }
  }, [nonFinancialTitles]);

  const validateInput = (value: number, questionId: string) => {
    if (!value) {
      setErrors((prev) => ({ ...prev, [questionId]: "ກະລຸນາໃສ່ຄະແນນ" }));
      return false;
    }
    if (value < 1 || value > 10) {
      setErrors((prev) => ({
        ...prev,
        [questionId]: "ຄະແນນຕ້ອງຢູ່ລະຫວ່າງ 1-10",
      }));
      return false;
    }
    setErrors((prev) => ({ ...prev, [questionId]: "" }));
    return true;
  };

  const handleInputChange = (
    questionId: string,
    value: string,
    field: "answer" | "description"
  ) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value,
      },
    }));
    if (field === "answer") {
      validateInput(Number(value), questionId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate all answers
      let validationErrors = false;
      const newErrors: { [key: string]: string } = {};

      nonFinancialTitles?.forEach((title) => {
        title.nonFinancialDetails.forEach((detail) => {
          const answer = Number(formData[detail.id]?.answer);
          if (!answer) {
            newErrors[detail.id] = "ກະລຸນາໃສ່ຄະແນນ";
            validationErrors = true;
          } else if (answer < 1 || answer > 10) {
            newErrors[detail.id] = "ຄະແນນຕ້ອງຢູ່ລະຫວ່າງ 1-10";
            validationErrors = true;
          }
        });
      });

      if (validationErrors) {
        setErrors(newErrors);
        toast.error("ກະລຸນາກວດສອບຄ່າຄະແນນໃຫ້ຖືກຕ້ອງ");
        return;
      }

      const payload = {
        fromId: localStorage.getItem("formId") || "",
        nonFinAnswerTitles: nonFinancialTitles?.map((title) => ({
          titleAnswer: title.title,
          nonFinAnswerDetails: title.nonFinancialDetails.map((detail) => ({
            question: detail.question,
            questionId: detail.id,
            answer: Number(formData[detail.id]?.answer || 0),
            description: formData[detail.id]?.description || null,
          })),
        })),
      };

      console.log("Submitting payload:", payload); // Debug log
      const response = await postAnswer(payload);
      console.log("Response:", response); // Debug log

      toast.success("ສົ່ງແບບຟອມສຳເລັດແລ້ວ");
      navigate({ to: "/report/non-financial-assessment" });
    } catch (error) {
      console.error("Error submitting answers:", error);
      toast.error(
        `ເກີດຂໍ້ຜິດພາດ: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <TableSkeleton />;
  if (!nonFinancialTitles) return <div>No data available</div>;

  return (
    <section>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>
              <Link replace to="/report/non-financial-assessment">
                ການປະເມີນ
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>ຕອບແບບຟອມການປະເມີນ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="mt-8">
        <CardHeader>
          <button
            type="button"
            onClick={() => {
              try {
                navigate({
                  to: "/report/non-financial-assessment",
                  replace: true,
                });
              } catch (error) {
                console.error("Navigation error:", error);
              }
            }}
            className="flex gap-1 items-center mb-4"
            aria-label="Go back to tax obligations report"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <CardTitle>ການປະເມີນຄະນະອຳນວຍການ</CardTitle>
          <CardDescription>ລາຍການການປະເມີນ</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            {nonFinancialTitles.map((title) => (
              <div key={title.id} className="my-6">
                <h3 className="text-lg font-semibold mb-4">{title.title}</h3>
                <Table className="w-full">
                  <TableHeader>
                    <TableRow className="[&>*]:font-bold [&>*]:text-black bg-gray-100 [&>*]:whitespace-nowrap">
                      <TableHead className="w-1/12 border">ລາດັບ</TableHead>
                      <TableHead className="border">ຄຳຖາມການປະເມີນ</TableHead>
                      <TableHead className="w-2/12 border">
                        ຄະແນນ (1-10)
                      </TableHead>
                      <TableHead className="border">ຄຳອະທິບາຍ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {title.nonFinancialDetails.map((detail, detailIndex) => (
                      <TableRow key={detail.id} className="cursor-pointer">
                        <TableCell className="font-medium border">
                          {detailIndex + 1}
                        </TableCell>
                        <TableCell className="font-medium border">
                          {detail.question}
                        </TableCell>
                        <TableCell className="font-medium border">
                          <Input
                            type="number"
                            min="1"
                            max="10"
                            placeholder="1-10"
                            value={formData[detail.id]?.answer || ""}
                            onChange={(e) =>
                              handleInputChange(
                                detail.id,
                                e.target.value,
                                "answer"
                              )
                            }
                            className={
                              errors[detail.id] ? "border-red-500" : ""
                            }
                          />
                          {errors[detail.id] && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors[detail.id]}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="font-medium border">
                          <Input
                            type="text"
                            placeholder="ຄຳອະທິບາຍເພີ່ມເຕີມ..."
                            value={formData[detail.id]?.description || ""}
                            onChange={(e) =>
                              handleInputChange(
                                detail.id,
                                e.target.value,
                                "description"
                              )
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}

            <div className="flex gap-x-6 justify-end mb-4 w-full">
              <Button
                type="button"
                className="bg-red-500 hover:bg-red-600"
                onClick={() =>
                  navigate({ to: "/report/non-financial-assessment" })
                }
              >
                ຍົກເລີກ
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "ກຳລັງສົ່ງ..." : "ສົ່ງແບບຟອມ"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
